import { saveUserEmail } from "./tvlUserApis";
import { popupStore } from "@/stores";

const TOAST_DURATION = 3000;

export const validateEmail = (email: string) => {
	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	return emailRegex.test(email);
};

export type SubscribeResult =
	| { ok: true; }
	| { ok: false; reason: "empty" | "invalid" | "no_user" | "network"; };

export type SubscribeWithEmailParams = {
	userId: number | undefined;
	deviceId: string;
	email: string;
	countryCode: string;
	timezone: string;
	language: string;
	email_consent: boolean;
	setEmail: (email: string) => void;
	successMessage: string;
	errorMessage: string;
	emptyMessage: string;
	invalidMessage: string;
	toastMode?: "default" | "silent";
};

/**
 * Creates or updates a user with the provided email and sets email consent.
 *
 * Returns a result rather than throwing so callers that submit alongside another action
 * (e.g. a search that navigates away) can decide whether to surface the failure.
 */
export async function submitEmailSubscription({
	userId,
	deviceId,
	email,
	countryCode,
	timezone,
	language,
	email_consent,
	setEmail,
	successMessage,
	errorMessage,
	emptyMessage,
	invalidMessage,
	toastMode = "default",
}: SubscribeWithEmailParams): Promise<SubscribeResult> {
	const shouldToast = toastMode === "default";

	if (!email) {
		if (shouldToast) popupStore.setContent(emptyMessage, true, TOAST_DURATION);
		return { ok: false, reason: "empty" };
	}

	if (!validateEmail(email)) {
		if (shouldToast) popupStore.setContent(invalidMessage, true, TOAST_DURATION);
		return { ok: false, reason: "invalid" };
	}

	// The user record is created asynchronously on first paint; a submit that lands before
	// it resolves has nothing to attach the email to.
	if (!userId || typeof userId !== "number") {
		if (shouldToast) popupStore.setContent(errorMessage, true, TOAST_DURATION);
		return { ok: false, reason: "no_user" };
	}

	try {
		await saveUserEmail({
			user_id: userId,
			device_id: deviceId,
			email: email,
			country_code: countryCode,
			timezone: timezone,
			language: language,
			subscribe: email_consent,
		});

		setEmail("");
		if (shouldToast) popupStore.setContent(successMessage, false, TOAST_DURATION);
		return { ok: true };
	} catch (error) {
		console.error("Failed to save user email:", error);
		if (shouldToast) popupStore.setContent(errorMessage, true, TOAST_DURATION);
		return { ok: false, reason: "network" };
	}
}
